import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  IconArrowLeft,
  IconArrowUp,
  IconCheck,
  IconPaperclip,
  IconSlash,
  IconX,
} from '@tabler/icons-react';
import type { CampaignListItemDto } from '@shared/services/apiService';
import { CampaignPicker } from './CampaignPicker';
import type { MissionSpec, WorkspaceAction } from './workspaceApi';

const actionLabels: Record<WorkspaceAction['kind'], string> = {
  'campaign-summary': 'Read campaign summary',
  'campaign-start': 'Start campaign',
  'campaign-stop': 'Stop campaign (drain)',
  'campaign-rename': 'Rename campaign',
  'webhook-dispatch': 'Dispatch configured webhooks',
};
const playbooks = [
  {
    name: 'Daily campaign summary',
    brief: 'Read the current campaign summary each morning.',
    timing: 'daily',
  },
  {
    name: 'Weekday campaign summary',
    brief: 'Read the current campaign summary on weekdays.',
    timing: 'weekdays',
  },
  {
    name: 'Weekly campaign summary',
    brief: 'Read the current campaign summary at the start of each week.',
    timing: 'weekly',
  },
  {
    name: 'After-call campaign summary',
    brief: 'Read the current campaign state after a call ends.',
    timing: 'call-ended',
  },
];
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Guided local authoring; approval transfers an unsaved, explicit native spec to Mission Studio. */
export const MissionPlanner = ({
  seed,
  canManage,
  onClose,
  onApprove,
  onDirtyChange,
}: {
  seed?: MissionSpec;
  canManage: boolean;
  onClose: () => void;
  onApprove: (spec: MissionSpec) => void;
  onDirtyChange: (dirty: boolean) => void;
}) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [brief, setBrief] = useState(seed?.brief || '');
  const [name, setName] = useState(seed?.name || '');
  const [timing, setTiming] = useState(() =>
    seed?.trigger.kind === 'call-ended'
      ? 'call-ended'
      : seed?.trigger.kind === 'schedule'
        ? 'custom'
        : 'manual',
  );
  const [time, setTime] = useState('09:00');
  const [weekday, setWeekday] = useState(1);
  const [cron, setCron] = useState(seed?.trigger.cron || '0 9 * * *');
  const [timezone, setTimezone] = useState(
    seed?.trigger.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [campaigns, setCampaigns] = useState<CampaignListItemDto[]>([]);
  const [actionKind, setActionKind] = useState<WorkspaceAction['kind']>('campaign-summary');
  const [title, setTitle] = useState('');
  const [contextOpen, setContextOpen] = useState(false);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const dirty =
    !!brief ||
    !!name ||
    campaigns.length > 0 ||
    timing !== 'manual' ||
    actionKind !== 'campaign-summary' ||
    !!title;
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  const choose = (playbook: (typeof playbooks)[number]) => {
    if (!canManage) return;
    setBrief(playbook.brief);
    setName(playbook.name);
    setTiming(playbook.timing);
    setActionKind('campaign-summary');
    setCommandsOpen(false);
    setAcknowledged(false);
  };
  const schedule = !['manual', 'call-ended'].includes(timing);
  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
  let validTimezone = true;
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone }).format();
  } catch {
    validTimezone = false;
  }
  const [hour, minute] = time.split(':').map(Number);
  const expression =
    timing === 'custom'
      ? cron
      : `${minute} ${hour} * * ${timing === 'weekdays' ? '1-5' : timing === 'weekly' ? weekday : '*'}`;
  const valid =
    !!name.trim() &&
    name.length <= 120 &&
    !!brief.trim() &&
    brief.length <= 4000 &&
    campaigns.length > 0 &&
    campaigns.length <= 8 &&
    (!schedule || (validTimezone && (timing === 'custom' ? !!cron.trim() : validTime))) &&
    (actionKind !== 'campaign-rename' || (!!title.trim() && title.length <= 120));
  const spec: MissionSpec = {
    name: name.trim(),
    brief: brief.trim(),
    trigger: schedule
      ? { kind: 'schedule', cron: expression, timezone }
      : { kind: timing === 'call-ended' ? 'call-ended' : 'manual' },
    actions: campaigns.map((campaign) => ({
      kind: actionKind,
      campaignId: campaign.id,
      ...(actionKind === 'campaign-rename' ? { title: title.trim() } : {}),
    })),
  };
  const content = (
    <Stack sx={{ height: '100%', minHeight: 0, bgcolor: 'background.paper' }}>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Callevate</Typography>
          <Typography variant="caption" color="text.secondary">
            Mission planner
          </Typography>
        </Box>
        <Chip label="Guided setup" size="small" variant="outlined" />
        <IconButton aria-label="Close mission planner" onClick={onClose}>
          <IconX size={18} />
        </IconButton>
      </Stack>
      <Stack gap={2.5} sx={{ p: 2.5, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {!canManage && (
          <Alert severity="info">
            Mission management permission is required to prepare a draft.
          </Alert>
        )}
        {!reviewing ? (
          <>
            <Typography component="h2" variant="h4">
              What would you like taken care of?
            </Typography>
            <Typography color="text.secondary">
              Describe your outcome, choose saved campaign context, and review exactly what this
              mission will do.
            </Typography>
            <Typography variant="overline">A few places to start</Typography>
            {playbooks.map((playbook) => (
              <Button
                key={playbook.name}
                disabled={!canManage}
                variant="outlined"
                sx={{ justifyContent: 'start', textAlign: 'start' }}
                onClick={() => choose(playbook)}>
                {playbook.name}
              </Button>
            ))}
          </>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.light' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {brief}
              </Typography>
            </Paper>
            <Typography variant="h5">Here’s the draft to review.</Typography>
            <Alert severity="info">
              Approval opens an unsaved Mission Studio draft. Save it explicitly, then review and
              publish to enable execution.
            </Alert>
            <TextField
              label="Mission name"
              value={name}
              disabled={!canManage}
              onChange={(event) => setName(event.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              select
              label="When should it run?"
              value={timing}
              disabled={!canManage}
              onChange={(event) => {
                setTiming(event.target.value);
                setAcknowledged(false);
              }}>
              {[
                ['manual', 'On demand'],
                ['daily', 'Every day'],
                ['weekdays', 'Weekdays'],
                ['weekly', 'Every week'],
                ['call-ended', 'After a call ends'],
                ['custom', 'Custom cron'],
              ].map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {schedule && (
              <>
                {timing === 'custom' ? (
                  <TextField
                    label="Five-field cron"
                    value={cron}
                    disabled={!canManage}
                    onChange={(event) => setCron(event.target.value)}
                  />
                ) : (
                  <TextField
                    label="Time"
                    type="time"
                    value={time}
                    disabled={!canManage}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) => setTime(event.target.value)}
                  />
                )}
                {timing === 'weekly' && (
                  <TextField
                    select
                    label="Day"
                    value={weekday}
                    disabled={!canManage}
                    onChange={(event) => setWeekday(Number(event.target.value))}>
                    {weekdays.map((day, index) => (
                      <MenuItem key={day} value={index}>
                        {day}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  label="IANA timezone"
                  value={timezone}
                  disabled={!canManage}
                  error={!validTimezone}
                  helperText={!validTimezone ? 'Use a valid IANA timezone.' : expression}
                  onChange={(event) => setTimezone(event.target.value)}
                />
              </>
            )}
            <Typography variant="h6">Explicit actions</Typography>
            <TextField
              select
              label="Action for each campaign"
              value={actionKind}
              disabled={!canManage}
              onChange={(event) => {
                setActionKind(event.target.value as WorkspaceAction['kind']);
                setAcknowledged(false);
              }}>
              {Object.entries(actionLabels).map(([kind, label]) => (
                <MenuItem key={kind} value={kind}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {actionKind === 'campaign-rename' && (
              <TextField
                label="New campaign title"
                value={title}
                disabled={!canManage}
                onChange={(event) => setTitle(event.target.value)}
                inputProps={{ maxLength: 120 }}
              />
            )}
            {campaigns.map((campaign) => (
              <Typography key={campaign.id}>
                {actionLabels[actionKind]} · {campaign.title}
                {actionKind === 'campaign-rename' ? ` → ${title || '(title required)'}` : ''}
              </Typography>
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  disabled={!canManage}
                  onChange={(_, checked) => setAcknowledged(checked)}
                />
              }
              label="I reviewed the explicit actions. The brief is descriptive and does not add other behavior."
            />
          </>
        )}
        {(contextOpen || reviewing) && (
          <Stack gap={1}>
            <Typography variant="h6">Workspace context</Typography>
            <Typography variant="body2" color="text.secondary">
              Attach up to eight saved campaigns. Each gets the action shown in the review.
            </Typography>
            <CampaignPicker
              value={null}
              disabled={!canManage || campaigns.length >= 8}
              onChange={(campaign) => {
                if (!canManage || !campaign) return;
                setCampaigns((previous) =>
                  previous.some((item) => item.id === campaign.id) || previous.length >= 8
                    ? previous
                    : [...previous, campaign],
                );
                setAcknowledged(false);
              }}
            />
            {campaigns.map((campaign) => (
              <Chip
                key={campaign.id}
                label={campaign.title}
                disabled={!canManage}
                onDelete={() => {
                  setCampaigns((previous) => previous.filter((item) => item.id !== campaign.id));
                  setAcknowledged(false);
                }}
              />
            ))}
            {!campaigns.length && (
              <Alert severity="info">Select at least one campaign before approval.</Alert>
            )}
          </Stack>
        )}
      </Stack>
      <Stack gap={1.5} sx={{ p: 2.5, borderTop: 1, borderColor: 'divider' }}>
        {reviewing ? (
          <Stack direction="row" gap={1}>
            <Button startIcon={<IconArrowLeft size={15} />} onClick={() => setReviewing(false)}>
              Edit brief
            </Button>
            <Button
              variant="contained"
              startIcon={<IconCheck size={15} />}
              disabled={!canManage || !valid || !acknowledged}
              onClick={() => {
                if (canManage && valid && acknowledged) onApprove(spec);
              }}
              sx={{ ml: 'auto' }}>
              Approve draft
            </Button>
          </Stack>
        ) : (
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canManage || !brief.trim() || brief.trim() === '/') return;
              setName(
                (previous) =>
                  previous ||
                  brief
                    .trim()
                    .split(/[\n.!?]/)[0]
                    .slice(0, 120),
              );
              setReviewing(true);
              setContextOpen(true);
              setCommandsOpen(false);
              setAcknowledged(false);
            }}>
            {commandsOpen && (
              <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                <Typography variant="caption">Mission playbooks</Typography>
                {playbooks.map((playbook) => (
                  <Button
                    key={playbook.name}
                    disabled={!canManage}
                    fullWidth
                    sx={{ justifyContent: 'start' }}
                    onClick={() => choose(playbook)}>
                    {playbook.name}
                  </Button>
                ))}
              </Paper>
            )}
            <TextField
              label="Mission brief"
              multiline
              minRows={3}
              maxRows={7}
              fullWidth
              value={brief}
              disabled={!canManage}
              inputProps={{ maxLength: 4000 }}
              placeholder="Describe an outcome to review…"
              onChange={(event) => {
                setBrief(event.target.value);
                if (event.target.value.endsWith('@')) setContextOpen(true);
                if (event.target.value.trim() === '/') setCommandsOpen(true);
              }}
            />
            <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
              <IconButton
                aria-label="Attach workspace context"
                disabled={!canManage}
                onClick={() => setContextOpen((value) => !value)}>
                <IconPaperclip size={17} />
              </IconButton>
              <IconButton
                aria-label="Mission commands"
                disabled={!canManage}
                onClick={() => setCommandsOpen((value) => !value)}>
                <IconSlash size={17} />
              </IconButton>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {campaigns.length ? `${campaigns.length} attached` : 'Add context · @'}
              </Typography>
              <Button
                type="submit"
                variant="contained"
                aria-label="Review mission draft"
                disabled={!canManage || !brief.trim() || brief.trim() === '/'}>
                <IconArrowUp size={18} />
              </Button>
            </Stack>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          Guided local setup. The brief is not sent to AI; only reviewed actions execute. Nothing is
          saved by approval.
        </Typography>
      </Stack>
    </Stack>
  );
  return desktop ? (
    <Box
      component="aside"
      aria-label="Mission planner"
      sx={{
        position: 'sticky',
        top: 12,
        height: 'calc(100dvh - 48px)',
        minHeight: 500,
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
      {content}
    </Box>
  ) : (
    <Drawer
      open
      anchor={theme.direction === 'rtl' ? 'left' : 'right'}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-label': 'Mission planner',
        sx: { width: { xs: '100%', sm: 460 } },
      }}>
      {content}
    </Drawer>
  );
};
